/**
 * Student-friendly translator (v6.36.22) — rule-based, deterministic.
 *
 * Translates raw WIDA levels + per-modality scores + growth deltas
 * into age-appropriate, encouraging language. Grade-banded tone:
 *   K-2:  cheerful + visual ("You're climbing the mountain!")
 *   3-5:  growth-mindset ("You're learning every day.")
 *   6-8:  curiosity + effort ("Stay curious. You're growing.")
 *   9-12: agency ("You're building real skill.")
 *
 * No emojis (per design guidelines — use lucide-react icons instead).
 * No LLM cost. No network calls. Pure deterministic functions.
 */

// Normalize "K", "1", "3-4", "5-6", "7-8", "9-12" → band key.
export function gradeToBand(grade) {
  const g = String(grade || '').toUpperCase().replace(/\s/g, '');
  if (!g) return '3_5';
  const tokens = g.replace(',', '-').split('-');
  const nums = tokens
    .map((t) => (t === 'K' ? 0 : Number.parseInt(t, 10)))
    .filter((n) => !Number.isNaN(n));
  if (nums.length === 0) return '3_5';
  const high = Math.max(...nums);
  if (high <= 2) return 'k2';
  if (high <= 5) return '3_5';
  if (high <= 8) return '6_8';
  return '9_12';
}

// WIDA level (1-6) → kid-friendly metaphor headline by band.
const LEVEL_METAPHORS = {
  k2: {
    1: 'You\'re just starting your adventure!',
    2: 'You\'re growing little by little, like a small plant!',
    3: 'You\'re getting stronger every day!',
    4: 'You\'re a super reader and speaker!',
    5: 'You\'re shining bright!',
    6: 'You\'re a true language star!',
  },
  '3_5': {
    1: 'You\'re at the start of your learning journey.',
    2: 'You\'re building your foundation, brick by brick.',
    3: 'You\'re climbing the mountain — keep going!',
    4: 'You\'re halfway up the mountain. Amazing work!',
    5: 'You\'re near the top — the view is great!',
    6: 'You\'ve reached the summit. Way to go!',
  },
  '6_8': {
    1: 'You\'re at the beginning. Every expert started here.',
    2: 'You\'re building your toolkit.',
    3: 'You\'re making real progress.',
    4: 'You\'re a confident communicator.',
    5: 'You\'re leading with your language skills.',
    6: 'You\'re demonstrating mastery.',
  },
  '9_12': {
    1: 'You\'re at the foundation phase of your language journey.',
    2: 'You\'re developing fluency and structure.',
    3: 'You\'re building academic language confidently.',
    4: 'You\'re communicating across complex topics.',
    5: 'You\'re operating at a strong academic level.',
    6: 'You\'re fully fluent in academic contexts.',
  },
};

// Per-modality Can-Do anchor by band + level. Kept short.
const CAN_DO = {
  k2: {
    listening: ['Listen to a story', 'Follow 2-step directions', 'Understand short conversations', 'Understand the main idea', 'Understand details', 'Understand complex stories'],
    speaking: ['Say single words', 'Use short phrases', 'Tell simple stories', 'Have full conversations', 'Explain your ideas', 'Speak like a star'],
    reading: ['Recognize letters', 'Read simple words', 'Read short books', 'Read longer books', 'Read with confidence', 'Read anything'],
    writing: ['Write your name', 'Write simple words', 'Write short sentences', 'Write a paragraph', 'Write a story', 'Write anything'],
  },
  '3_5': {
    listening: ['Pick up key words', 'Follow short directions', 'Understand the main idea', 'Follow class discussions', 'Understand detailed talks', 'Listen like a pro'],
    speaking: ['Use everyday words', 'Speak in simple sentences', 'Tell about pictures', 'Explain your thinking', 'Hold full conversations', 'Speak like a leader'],
    reading: ['Read familiar words', 'Read short passages', 'Understand stories', 'Read for information', 'Read with strong fluency', 'Read like an expert'],
    writing: ['Write words and phrases', 'Write simple sentences', 'Write short paragraphs', 'Write multi-paragraph pieces', 'Write detailed essays', 'Write like an author'],
  },
  '6_8': {
    listening: ['Understand simple speech', 'Follow class directions', 'Follow most discussions', 'Understand academic content', 'Pick up subtle meaning', 'Listen at an expert level'],
    speaking: ['Use everyday phrases', 'Talk about familiar topics', 'Discuss class topics', 'Express opinions with detail', 'Lead discussions', 'Speak across any topic'],
    reading: ['Recognize key vocabulary', 'Read simple texts', 'Read grade-level material', 'Read academic articles', 'Read complex texts', 'Read at an advanced level'],
    writing: ['Write simple notes', 'Write short paragraphs', 'Write structured paragraphs', 'Write multi-paragraph essays', 'Write polished arguments', 'Write at an expert level'],
  },
  '9_12': {
    listening: ['Understand basic speech', 'Follow short lectures', 'Follow most academic content', 'Grasp nuanced ideas', 'Understand specialized content', 'Listen at a college-ready level'],
    speaking: ['Use everyday phrases', 'Discuss familiar topics', 'Discuss academic content', 'Defend claims with evidence', 'Speak with full fluency', 'Speak at a college-ready level'],
    reading: ['Read simple texts', 'Read grade-level texts', 'Read academic articles', 'Read complex literary works', 'Read scholarly content', 'Read at a college-ready level'],
    writing: ['Write short responses', 'Write structured paragraphs', 'Write academic essays', 'Write evidence-based arguments', 'Write polished long-form pieces', 'Write at a college-ready level'],
  },
};

// Growth narrative by band — tone shifts with age.
function growthNarrative(band, change) {
  if (change >= 0.8) {
    return {
      k2: 'WOW! You jumped up so much! You\'re working SO hard.',
      '3_5': 'Big growth! Your hard work is really paying off.',
      '6_8': 'Significant growth. Your effort is paying off.',
      '9_12': 'Substantial growth this period — your consistency is showing.',
    }[band];
  }
  if (change >= 0.3) {
    return {
      k2: 'You\'re growing! Keep practicing!',
      '3_5': 'Nice growth — you\'re moving in the right direction.',
      '6_8': 'You\'re progressing. Keep stretching yourself.',
      '9_12': 'Steady progress. Stay focused on your stretch areas.',
    }[band];
  }
  if (change > 0) {
    return {
      k2: 'A little growth! Every bit counts!',
      '3_5': 'Small step forward — every step matters.',
      '6_8': 'Slight gain. Look for one habit to push harder on.',
      '9_12': 'Marginal gain. Identify one focus area to accelerate.',
    }[band];
  }
  if (change === 0) {
    return {
      k2: 'You\'re holding steady! Let\'s try a new challenge!',
      '3_5': 'You\'re holding steady — time to stretch into something new.',
      '6_8': 'Plateau. A new strategy or challenge will help you climb again.',
      '9_12': 'Stable score — consider what new challenge unlocks the next level.',
    }[band];
  }
  // change < 0
  return {
    k2: 'It\'s okay! Some days are harder. Let\'s practice together!',
    '3_5': 'It\'s okay to have a slower day. Let\'s figure out what to practice.',
    '6_8': 'Score dipped — happens to everyone. Pick one area to focus on next.',
    '9_12': 'Dip noted. Use this as data — identify what changed and recalibrate.',
  }[band];
}

// Next-step suggestion based on the weakest modality.
function nextStep(band, weakest) {
  if (!weakest) return null;
  const tips = {
    listening: {
      k2: 'Listen to a story every day with your family!',
      '3_5': 'Try listening to a short podcast or audiobook this week.',
      '6_8': 'Listen to an educational podcast or news clip daily.',
      '9_12': 'Practice listening to academic lectures or news segments without pausing.',
    },
    speaking: {
      k2: 'Tell someone about your day in 3 sentences!',
      '3_5': 'Practice saying "because" sentences to connect your ideas.',
      '6_8': 'Practice explaining a topic from class out loud to a friend.',
      '9_12': 'Record yourself answering an academic prompt and listen back for clarity.',
    },
    reading: {
      k2: 'Read a fun book with someone for 10 minutes!',
      '3_5': 'Read for 15 minutes a day — pick books you love.',
      '6_8': 'Read one non-fiction article a day and write one summary sentence.',
      '9_12': 'Read challenging academic texts daily and annotate key claims.',
    },
    writing: {
      k2: 'Draw a picture and write 2 sentences about it!',
      '3_5': 'Write a short paragraph every day — even just 3-4 sentences.',
      '6_8': 'Write a structured paragraph (topic sentence + evidence + conclusion) daily.',
      '9_12': 'Practice writing thesis-driven paragraphs with cited evidence.',
    },
  };
  return tips[weakest]?.[band] || null;
}

/**
 * Translate a student progress payload into a kid-friendly summary.
 * @param {Object} args
 * @param {number} args.gradeLevel  — raw grade string ("3-4", "K", etc.)
 * @param {Object} args.summary     — the /api/student/progress summary object
 * @returns {Object} { headline, levelLabel, growthLine, nextStepLine, canDoByModality }
 */
export function translateProgressForStudent({ gradeLevel, summary }) {
  const band = gradeToBand(gradeLevel);
  const level = Math.min(Math.max(Math.round(summary?.latest_wida_level || 1), 1), 6);
  const headline = LEVEL_METAPHORS[band][level];
  const growthLine = growthNarrative(band, Number(summary?.overall_change || 0));
  const nextStepLine = nextStep(band, summary?.weakest_domain);
  const levelLabel = `Level ${level} — ${summary?.latest_wida_name || ''}`.trim();

  // Per-modality Can-Do anchors (uses the latest level from progress
  // summary — we don't have per-modality levels here so use composite).
  const canDoByModality = {};
  for (const m of ['listening', 'speaking', 'reading', 'writing']) {
    canDoByModality[m] = CAN_DO[band][m][level - 1] || CAN_DO[band][m][0];
  }

  return { band, level, levelLabel, headline, growthLine, nextStepLine, canDoByModality };
}

/**
 * Translate a single graded feedback item into kid-friendly language.
 * @param {Object} args
 * @param {string} args.gradeLevel  — raw grade string
 * @param {string} args.modality
 * @param {number} args.score
 * @param {number} args.maxScore
 */
export function translateFeedbackForStudent({ gradeLevel, modality, score, maxScore }) {
  const band = gradeToBand(gradeLevel);
  const pct = maxScore > 0 ? (score / maxScore) * 100 : 0;

  let mood;
  if (pct >= 80) {
    mood = {
      k2: 'AWESOME! You did so well!',
      '3_5': 'Excellent work! You really showed what you know.',
      '6_8': 'Strong response — you\'re clearly proficient here.',
      '9_12': 'High-quality response. Your command of the task shows.',
    }[band];
  } else if (pct >= 50) {
    mood = {
      k2: 'Great job! Keep practicing!',
      '3_5': 'Solid work — and there\'s room to grow.',
      '6_8': 'Good response. A few targeted improvements will lift this higher.',
      '9_12': 'Competent response. Refine the depth and structure for stronger results.',
    }[band];
  } else if (pct >= 25) {
    mood = {
      k2: 'Good try! Let\'s practice this together!',
      '3_5': 'Nice try — let\'s find one thing to focus on next time.',
      '6_8': 'You\'re on your way. Target one specific skill to grow.',
      '9_12': 'Developing response. Identify the specific skill to invest in next.',
    }[band];
  } else {
    mood = {
      k2: 'Don\'t give up! We\'ll practice this!',
      '3_5': 'This is a stretch goal — mistakes mean you\'re learning.',
      '6_8': 'Challenge spotted. Use this as your growth target.',
      '9_12': 'Stretch area identified. Let\'s build a plan to strengthen it.',
    }[band];
  }

  const focus = nextStep(band, modality);
  return { band, mood, focus };
}
